import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ReportImageDialog } from "@/components/ReportImageDialog";
import { Send, Heart, Settings, Menu, Users, Plus, User, MessageCircle, Crown, Search, HeartHandshake, Eye, MapPin, Calendar, Star, FileImage, Flame, Lock, Coins, Zap, Check, ArrowLeft, Trash2, Camera, Mic, Square, HelpCircle, X, Copy, Bug, Flag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { MessageLimitModal } from "@/components/MessageLimitModal";
import { MicDisclosureModal } from "@/components/MicDisclosureModal";
import { AiDisclosureModal } from "@/components/AiDisclosureModal";
import {
  getAiDisclosureAccepted,
  setAiDisclosureAccepted,
  getMessageMenuCoachDismissed,
  setMessageMenuCoachDismissed,
} from "@/lib/uiHintsPreferences";
import { PremiumFeatureModal } from "@/components/PremiumFeatureModal";
import { apiClient, isAuthSessionExpiredError } from "@/api/ApiClient";
import { consumeSseStream } from "@/pages/chatSseStream";
import { hrefDebugMessageExchange } from "@/lib/routerBasename";
import { isDebug } from "@/config/env";

import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";

import sampleCoupleHug from "@/assets/sample-couple-hug.jpg";

// Avatar logic removed - using URLs from server

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  imageUrl?: string;
  audioUrl?: string;
  mimeType?: string;
  durationMs?: number;
  hasImage?: boolean;
  isBlurred?: boolean;
  messageExchangeId?: string;
  isReported?: boolean;
  actions?: ChatMessageAction[];
}

// Import models from API package
import { BoyfriendProfile, ApiLastMessage, ApiChatItem, ApiChatsResponse, ApiMessage, type ChatMessageAction } from '@/api/models';

// Use BoyfriendProfile as Contact
type Contact = BoyfriendProfile;

interface ChatPageProps {
  user: any;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCharacterCreation: () => void;
  onNavigateToExplore: () => void;
  onNavigateToPremium: () => void;
  onNavigateToProfilePage: (boyfriendId: string) => void;
  onPromptLogin: () => void;
  onLogout: () => void;
  newCharacter: any;
  selectedBoyfriendId: string | null;
  onTokensClick: () => void;
  userTokens: number;
  onPurchaseTokens: (amount: number, method: string) => void;
  favorites: Set<string>;
  onToggleFavorite: (boyfriendId: string) => Promise<void>;
  onBottomNavVisibilityChange?: (visible: boolean) => void;
  onBackToList: () => void;
}

export const ChatPage = ({ 
  user,
  onNavigateToMyBoyfriends,
  onNavigateToProfile, 
  onNavigateToSettings, 
  onNavigateToCharacterCreation,
  onNavigateToExplore,
  onNavigateToPremium,
  onNavigateToProfilePage,
  onPromptLogin,
  onLogout,
  newCharacter,
  selectedBoyfriendId,
  onTokensClick,
  userTokens,
  onPurchaseTokens,
  favorites,
  onToggleFavorite,
  onBottomNavVisibilityChange,
  onBackToList
}: ChatPageProps) => {
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // The input bar floats over the message list; its height (suggestion
  // chips + input + safe-area) becomes the list's bottom padding so the
  // newest message is never hidden behind it.
  const [inputBarHeight, setInputBarHeight] = useState(0);
  const inputBarObserverRef = useRef<ResizeObserver | null>(null);
  const inputBarRef = useCallback((el: HTMLDivElement | null) => {
    inputBarObserverRef.current?.disconnect();
    inputBarObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setInputBarHeight(el.offsetHeight));
    observer.observe(el);
    inputBarObserverRef.current = observer;
    setInputBarHeight(el.offsetHeight);
  }, []);
  const voiceMediaStreamRef = useRef<MediaStream | null>(null);
  const voiceMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceChunksRef = useRef<BlobPart[]>([]);
  const voiceRecordingUnmountRef = useRef(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatStreamAbortRef = useRef<AbortController | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressOriginRef = useRef({ x: 0, y: 0 });
  /** Počet nových BF odpovědí od streamu; při >= 3 → persistCoachDismissal = uložený pref navždy (už se coach neukáže). */
  const coachBoyfriendRepliesSinceShownRef = useRef(0);

  const [messageContextMenu, setMessageContextMenu] = useState<{
    x: number;
    y: number;
    message: Message;
  } | null>(null);

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showVoicePremiumModal, setShowVoicePremiumModal] = useState(false);
  const [showMicDisclosure, setShowMicDisclosure] = useState(false);
  const [showAiDisclosure, setShowAiDisclosure] = useState(false);
  const [coachPrefsLoaded, setCoachPrefsLoaded] = useState(false);
  const [menuCoachDismissed, setMenuCoachDismissed] = useState(false);
  const [limitResetTime, setLimitResetTime] = useState<Date | null>(null);
  const [userStyle, setUserStyle] = useState<'anime' | 'realistic'>('realistic');
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Avatar URLs used directly from server

  // Initialize messages and contacts from API
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [chatContacts, setChatContacts] = useState<Contact[]>([]);

  const [selectedChat, setSelectedChat] = useState<string | null>(selectedBoyfriendId || null);
  const [swipedChatId, setSwipedChatId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  
  // Pagination state for infinite scroll
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestMessageId, setOldestMessageId] = useState<string | null>(null);
  const [voiceCaptureMode, setVoiceCaptureMode] = useState<'idle' | 'recording' | 'uploading'>('idle');

  // Command mapping: map translated text to commands
  const commandMap: Record<string, string> = {
    [t('chat.suggestion_send_photo')]: '#[SEND_PHOTO]#'
  };

  // Animated loading dots component
  const LoadingDots = () => (
    <div className="flex items-center space-x-1">
      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  );


  // Helper function to format message time
  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return "Now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Load messages with pagination support
  // Returns true if chat was empty (for first message detection)
  const MESSAGES_PAGE_SIZE = 20;

  const loadMessages = async (boyfriendId: string, beforeId?: string): Promise<boolean> => {
    try {
      const url = beforeId 
        ? `/chats/${boyfriendId}/messages?beforeId=${beforeId}&limit=${MESSAGES_PAGE_SIZE}`
        : `/chats/${boyfriendId}/messages?limit=${MESSAGES_PAGE_SIZE}`;
        
      const messagesData = await apiClient.get(url);
      console.log('ChatPage - messages data:', messagesData);
      console.log('First message structure:', messagesData[0]);
      
      if (messagesData && Array.isArray(messagesData)) {
        // Convert API messages to our Message format
        // Each API message contains both user message and AI response
        const formattedMessages: Message[] = [];
        
        messagesData.forEach((apiMsg: ApiMessage) => {
          console.log('Processing API message:', apiMsg);
          const reported = !!(apiMsg as ApiMessage & { reported?: boolean }).reported;
          const reportedLabel = t('chat.message_reported');

          if (reported) {
            const ext = apiMsg as ApiMessage & { imageUrl?: string };
            if (apiMsg.response || ext.imageUrl) {
              formattedMessages.push({
                id: `${apiMsg.id}_ai`,
                text: reportedLabel,
                sender: 'ai',
                timestamp: new Date(apiMsg.createdAt),
                imageUrl: undefined,
                hasImage: false,
                isBlurred: false,
                messageExchangeId: String(apiMsg.id),
                isReported: true
              });
            }
            if (apiMsg.message) {
              let displayText = apiMsg.message;
              const commandMatch = displayText.match(/^#\[([A-Z_]+)\]#\s*/);
              if (commandMatch) {
                displayText = displayText.substring(commandMatch[0].length);
              }
              formattedMessages.push({
                id: `${apiMsg.id}_user`,
                text: displayText,
                sender: 'user',
                timestamp: new Date(apiMsg.createdAt),
                messageExchangeId: String(apiMsg.id),
                isReported: false
              });
            }
            return;
          }

          // Add AI response first (it will appear above user message with column-reverse)
          if (apiMsg.response || (apiMsg as any).imageUrl) {
            formattedMessages.push({
              id: `${apiMsg.id}_ai`,
              text: (apiMsg as any).imageUrl ? undefined as any : apiMsg.response,
              sender: 'ai',
              timestamp: new Date(apiMsg.createdAt),
              imageUrl: (apiMsg as any).imageUrl || undefined,
              hasImage: !!(apiMsg as any).imageUrl,
              isBlurred: false,
              messageExchangeId: apiMsg.messageExchangeId || apiMsg.id, // Use message ID as messageExchangeId for now
              actions: apiMsg.responseActions
            });
          }
          
          // Add user message second (it will appear below AI response with column-reverse)
          if (apiMsg.messageType === 'audio' && apiMsg.audioUrl) {
            formattedMessages.push({
              id: `${apiMsg.id}_user`,
              text: '',
              sender: 'user',
              timestamp: new Date(apiMsg.createdAt),
              audioUrl: apiMsg.audioUrl,
              mimeType: apiMsg.mimeType,
              durationMs: apiMsg.durationMs,
              messageExchangeId: String(apiMsg.id)
            });
          } else if (apiMsg.message) {
            // Remove command from beginning of message if present (format: #[COMMAND]# text)
            let displayText = apiMsg.message;
            const commandMatch = displayText.match(/^#\[([A-Z_]+)\]#\s*/);
            if (commandMatch) {
              // Remove command and following space from display text
              displayText = displayText.substring(commandMatch[0].length);
            }
            
            formattedMessages.push({
              id: `${apiMsg.id}_user`,
              text: displayText,
              sender: 'user',
              timestamp: new Date(apiMsg.createdAt),
              imageUrl: undefined,
              hasImage: false,
              isBlurred: false,
              messageExchangeId: String(apiMsg.id)
            });
          }
        });
        
        // Sort by timestamp (newest first) - because we use column-reverse
        formattedMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        if (beforeId) {
          // Loading more messages - append older messages to the end (they'll appear at top with column-reverse)
          setMessages(prev => ({
            ...prev,
            [boyfriendId]: [...(prev[boyfriendId] || []), ...formattedMessages]
          }));
        } else {
          // Initial load - replace messages (newest first)
          setMessages(prev => ({
            ...prev,
            [boyfriendId]: formattedMessages
          }));
        }
        
        // Update pagination state
        if (formattedMessages.length > 0) {
          // Use the original API message ID for beforeId (not the formatted message ID)
          const lastApiMessage = messagesData[messagesData.length - 1];
          setOldestMessageId(lastApiMessage.id);
          setHasMoreMessages(messagesData.length >= MESSAGES_PAGE_SIZE);
          return false; // Chat is not empty
        } else {
          setHasMoreMessages(false);
          return true; // Chat is empty
        }
      } else {
        // No messages yet, initialize empty
        setMessages(prev => ({
          ...prev,
          [boyfriendId]: []
        }));
        setHasMoreMessages(false);
        return true; // Chat is empty
      }
    } catch (messageError) {
      console.error('Error loading messages:', messageError);
      // Initialize with empty messages on error
      setMessages(prev => ({
        ...prev,
        [boyfriendId]: []
      }));
      setHasMoreMessages(false);
      return true; // Consider empty on error
    }
  };

  // Load more messages (for infinite scroll)
  const loadMoreMessages = async () => {
    if (!selectedChat || isLoadingMore || !hasMoreMessages || !oldestMessageId) return;
    
    setIsLoadingMore(true);
    try {
      await loadMessages(selectedChat, oldestMessageId);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Load specific boyfriend profile and messages
  const loadBoyfriendData = async () => {
    if (!user?.id || !selectedBoyfriendId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setApiError(null);
      
      // Load boyfriend profile
      const boyfriendData = await apiClient.get(`/boyfriends/${selectedBoyfriendId}`);
      console.log('ChatPage - boyfriend profile:', boyfriendData);
      
      if (boyfriendData) {
        setChatContacts([boyfriendData]);
        setSelectedContact(boyfriendData);
        
        // Load messages for this specific chat
        const isEmpty = await loadMessages(selectedBoyfriendId);
        
        // Check if chat is empty and send first message command
        if (isEmpty) {
          console.log('Chat is empty, sending first message command');
          // Automatically send first message command
          handleSendMessage('#[SEND_FIRST_MESSAGE]#');
        }
        
        // Mark messages as read
        try {
          await apiClient.post(`/chats/${selectedBoyfriendId}/mark-as-read`, {});
          console.log('Messages marked as read for boyfriend:', selectedBoyfriendId);
        } catch (markReadError) {
          console.error('Error marking messages as read:', markReadError);
          // Don't fail the whole load if marking as read fails
        }
      }
    } catch (error) {
      console.error('Error loading boyfriend data:', error);
      if (isAuthSessionExpiredError(error)) return;
      setApiError('Failed to load chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const [inputMessage, setInputMessage] = useState("");
  const [maxMessageLength, setMaxMessageLength] = useState(350);
  const [debugChatPromptsEnabled, setDebugChatPromptsEnabled] = useState(false);
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(favorites);

  // Load public config (max message length; server can also enable debug menu alongside VITE_DEBUG_MODE)
  useEffect(() => {
    apiClient.getPublicConfig().then((config: { maxMessageLength?: number; debugChatPromptsEnabled?: boolean }) => {
      if (config?.maxMessageLength != null && config.maxMessageLength > 0) {
        setMaxMessageLength(config.maxMessageLength);
      }
      // Accept any server truthy (boolean true, etc.); sync false when API returns false
      setDebugChatPromptsEnabled(Boolean(config?.debugChatPromptsEnabled));
    }).catch(() => {});
  }, []);

  const [showPrivateContentModal, setShowPrivateContentModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showTokenPaymentModal, setShowTokenPaymentModal] = useState(false);
  const [showPaymentFormModal, setShowPaymentFormModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'crypto'>('card');
  const [selectedTokenPackage, setSelectedTokenPackage] = useState<'small' | 'medium' | 'large'>('small');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState<{ id: string; url: string }[]>([]);
  const [reportImageId, setReportImageId] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeImageUrl, setActiveImageUrl] = useState<string | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportPendingExchangeId, setReportPendingExchangeId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('SPAM');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [uploadedUserPhoto, setUploadedUserPhoto] = useState<string>('');
  const [showGenerationDialog, setShowGenerationDialog] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [generatedImageId, setGeneratedImageId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [modalTokens, setModalTokens] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([getAiDisclosureAccepted(), getMessageMenuCoachDismissed()]).then(
      ([aiAccepted, coachDismissed]) => {
        if (cancelled) return;
        if (!aiAccepted) setShowAiDisclosure(true);
        setMenuCoachDismissed(coachDismissed);
        if (!coachDismissed) {
          coachBoyfriendRepliesSinceShownRef.current = 0;
        }
        setCoachPrefsLoaded(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const persistCoachDismissal = useCallback(async () => {
    await setMessageMenuCoachDismissed();
    setMenuCoachDismissed(true);
    coachBoyfriendRepliesSinceShownRef.current = 0;
  }, []);

  /** Opening the message context menu (long-press) dismisses the coach mark permanently. */
  useEffect(() => {
    if (!messageContextMenu) return;
    void persistCoachDismissal();
  }, [messageContextMenu, persistCoachDismissal]);

  useEffect(() => {
    const onHintsReset = () => {
      void Promise.all([getAiDisclosureAccepted(), getMessageMenuCoachDismissed()]).then(
        ([aiAccepted, coachDismissed]) => {
          if (!aiAccepted) setShowAiDisclosure(true);
          else setShowAiDisclosure(false);
          setMenuCoachDismissed(coachDismissed);
          if (!coachDismissed) {
            coachBoyfriendRepliesSinceShownRef.current = 0;
          }
          setCoachPrefsLoaded(true);
        }
      );
    };
    window.addEventListener("dreamboy-ui-hints-reset", onHintsReset);
    return () => window.removeEventListener("dreamboy-ui-hints-reset", onHintsReset);
  }, []);

  const handleSelectChat = (contactId: string) => {
    console.log('handleSelectChat called with contactId:', contactId);
    const contact = chatContacts.find(c => c.id === contactId);
    if (contact) {
      console.log('Setting selectedChat to:', contactId, 'and selectedContact to:', contact.name);
      setSelectedChat(contactId);
      setSelectedContact(contact);
      
      if (!isMobile && (window as any).collapseSidebar) {
        (window as any).collapseSidebar();
      }
    }
  };

  const handleDebugPrompt = (messageId: string) => {
    // Find the message to get its messageExchangeId
    const message = messages[selectedChat]?.find(msg => msg.id === messageId);
    console.log('Debug prompt clicked for message:', message);
    console.log('Message exchange ID:', message?.messageExchangeId);
    
    if (message?.isReported) {
      return;
    }
    if (message?.messageExchangeId) {
      window.open(hrefDebugMessageExchange(message.messageExchangeId), '_blank', 'noopener,noreferrer');
    } else {
      toast({
        title: "Debug info not available",
        description: "This message doesn't have debug information available.",
        variant: "destructive"
      });
    }
  };

  const submitChatReport = async () => {
    if (!reportPendingExchangeId || !selectedChat) return;
    setReportSubmitting(true);
    try {
      await apiClient.reportChatMessage(selectedChat, reportPendingExchangeId, reportReason);
      const label = t('chat.message_reported');
      setMessages(prev => ({
        ...prev,
        [selectedChat]: (prev[selectedChat] || []).map(msg =>
          msg.messageExchangeId === reportPendingExchangeId && msg.sender === 'ai'
            ? { ...msg, text: label, isReported: true, imageUrl: undefined, hasImage: false }
            : msg
        )
      }));
      toast({ title: t('chat.report_submitted') });
      setReportDialogOpen(false);
      setReportPendingExchangeId(null);
    } catch (e) {
      console.error('Report failed', e);
      toast({ title: t('chat.report_failed'), variant: 'destructive' });
    } finally {
      setReportSubmitting(false);
    }
  };

  const LONG_PRESS_MS = 550;
  const LONG_PRESS_MOVE_PX = 12;

  const getDisplayTextForCopy = (message: Message): string => {
    if (!message.text || message.text === 'LOADING_DOTS') return '';
    let displayText = message.text;
    if (message.sender === 'user') {
      const commandMatch = displayText.match(/^#\[([A-Z_]+)\]#\s*/);
      if (commandMatch) displayText = displayText.substring(commandMatch[0].length);
    }
    return displayText;
  };

  const clampMenuPosition = (x: number, y: number) => {
    const margin = 12;
    const halfW = 100;
    const nx = Math.min(Math.max(margin + halfW, x), window.innerWidth - margin - halfW);
    const ny = Math.min(Math.max(margin + 24, y), window.innerHeight - margin);
    return { x: nx, y: ny };
  };

  const endMessageLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const messageBubblePointerHandlers = (message: Message): React.HTMLAttributes<HTMLDivElement> => ({
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (message.text === 'LOADING_DOTS') return;
      if (message.isReported) return;
      setMessageContextMenu(null);
      endMessageLongPress();
      longPressOriginRef.current = { x: e.clientX, y: e.clientY };
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        window.getSelection()?.removeAllRanges();
        const { x, y } = clampMenuPosition(longPressOriginRef.current.x, longPressOriginRef.current.y);
        setMessageContextMenu({ x, y, message });
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      if (!longPressTimerRef.current) return;
      const o = longPressOriginRef.current;
      const dx = e.clientX - o.x;
      const dy = e.clientY - o.y;
      if (dx * dx + dy * dy > LONG_PRESS_MOVE_PX * LONG_PRESS_MOVE_PX) {
        endMessageLongPress();
      }
    },
    onPointerUp: endMessageLongPress,
    onPointerLeave: endMessageLongPress,
    onPointerCancel: endMessageLongPress,
  });

  useEffect(() => {
    if (!messageContextMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMessageContextMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [messageContextMenu]);

  const runChatMessageAction = (type: string) => {
    switch (type) {
      case 'open_token_purchase':
        try { window.dispatchEvent(new CustomEvent('open-token-modal')); } catch {}
        onTokensClick?.();
        break;
      default:
        break;
    }
  };

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || !selectedChat) return;

    // Check max message length (also enforced on backend)
    const textToSend = messageText.trim();
    if (textToSend.length > maxMessageLength) {
      toast({
        title: t('chat.message_too_long') || 'Message too long',
        description: (t('chat.max_characters') || 'Maximum {{count}} characters allowed').replace('{{count}}', String(maxMessageLength)),
        variant: 'destructive'
      });
      return;
    }

    // Message limits are checked on backend in ChatController.checkChatLimits()
    // If limit is exceeded, backend will send premium-upgrade-required event

    // Check if this is a command - if mapped, prepend command to message text
    const command = commandMap[messageText];
    // If command exists, prepend it to the message text; otherwise use original text
    const apiText = command ? `${command} ${messageText}` : messageText;
    // Display original text in chat (for user-friendly display)
    const displayText = messageText;

    // For system commands like #[SEND_FIRST_MESSAGE]#, don't show user message
    const isSystemCommand = messageText.startsWith('#[') && messageText.endsWith(']#');
    
    const newMessage: Message = {
      id: Date.now().toString(),
      text: displayText,
      sender: 'user',
      timestamp: new Date()
    };

    // Add new message at the beginning (it will appear at bottom with column-reverse)
    // Skip adding user message for system commands
    if (!isSystemCommand) {
      setMessages(prev => ({
        ...prev,
        [selectedChat]: [newMessage, ...(prev[selectedChat] || [])]
      }));
    }

    setInputMessage("");

    // POST + fetch SSE (Bearer token); EventSource is GET-only
    try {
      const aiMessageId = (Date.now() + 1).toString();
      let aiResponseText = '';

      const aiMessage: Message = {
        id: aiMessageId,
        text: 'LOADING_DOTS',
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => ({
        ...prev,
        [selectedChat]: [aiMessage, ...(prev[selectedChat] || [])]
      }));

      if (!menuCoachDismissed && coachPrefsLoaded) {
        coachBoyfriendRepliesSinceShownRef.current += 1;
        if (coachBoyfriendRepliesSinceShownRef.current >= 3) {
          void persistCoachDismissal(); /* uloží pref → coach už nikdy */
        }
      }

      chatStreamAbortRef.current?.abort();
      const ac = new AbortController();
      chatStreamAbortRef.current = ac;

      const streamUrl = `${apiClient.baseURL}/chat/stream`;
      const chatId = selectedChat as string;

      const appendDefaultChunk = (raw: string) => {
        if (raw === '[DONE]') return true;
        try {
          const decodedBytes = atob(raw);
          const decodedText = decodeURIComponent(escape(decodedBytes));
          aiResponseText += decodedText;
        } catch {
          aiResponseText += raw;
        }
        setMessages(prev => ({
          ...prev,
          [chatId]: prev[chatId].map(msg =>
            msg.id === aiMessageId ? { ...msg, text: aiResponseText } : msg
          )
        }));
        return false;
      };

      const setAiErrorText = () => {
        setMessages(prev => ({
          ...prev,
          [chatId]: prev[chatId].map(msg =>
            msg.id === aiMessageId
              ? { ...msg, text: "Sorry, I'm having trouble responding right now. Please try again." }
              : msg
          )
        }));
      };

      const handleSseEvent = (eventName: string, data: string): boolean => {
        switch (eventName) {
          case 'uuid':
            setMessages(prev => ({
              ...prev,
              [chatId]: prev[chatId].map(msg => {
                if (msg.id === aiMessageId) return { ...msg, messageExchangeId: data };
                if (!isSystemCommand && msg.id === newMessage.id) return { ...msg, messageExchangeId: data };
                return msg;
              })
            }));
            return false;
          case 'image':
            try {
              setMessages(prev => ({
                ...prev,
                [chatId]: prev[chatId].map(msg =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        imageUrl: data,
                        hasImage: true,
                        text: msg.text === 'LOADING_DOTS' ? undefined : msg.text
                      }
                    : msg
                )
              }));
            } catch (e) {
              console.error('Failed to handle image event', e);
            }
            ac.abort();
            return true;
          case 'done':
            ac.abort();
            return true;
          case 'text':
            try {
              setMessages(prev => ({
                ...prev,
                [chatId]: prev[chatId].map(msg =>
                  msg.id === aiMessageId ? { ...msg, text: data } : msg
                )
              }));
            } catch (e) {
              console.error('Failed to handle text event', e);
            }
            return false;
          case 'insufficient-tokens': {
            let text: string;
            let actions: ChatMessageAction[] | undefined;
            try {
              const parsed = JSON.parse(data) as { message?: string; actions?: ChatMessageAction[] };
              if (parsed && typeof parsed.message === 'string' && parsed.message.length > 0) {
                text = parsed.message;
              } else {
                const tr = t('chat.insufficient_tokens');
                text = tr === 'chat.insufficient_tokens' ? 'Sorry, you don\'t have enough tokens.' : tr;
              }
              if (parsed?.actions && Array.isArray(parsed.actions) && parsed.actions.length > 0) {
                actions = parsed.actions;
              }
            } catch {
              const tr = t('chat.insufficient_tokens');
              text = tr === 'chat.insufficient_tokens' ? (data || 'Sorry, you don\'t have enough tokens.') : tr;
            }
            setMessages(prev => ({
              ...prev,
              [chatId]: prev[chatId].map(msg =>
                msg.id === aiMessageId ? { ...msg, text, actions } : msg
              )
            }));
            try { window.dispatchEvent(new CustomEvent('open-token-modal')); } catch {}
            try { onTokensClick && onTokensClick(); } catch {}
            ac.abort();
            return true;
          }
          case 'message-too-long': {
            let messageText = 'Message too long. Maximum 350 characters allowed.';
            try {
              const parsed = JSON.parse(data);
              if (parsed.message) messageText = parsed.message;
            } catch { /* use default */ }
            toast({ title: messageText, variant: 'destructive' });
            setMessages(prev => ({
              ...prev,
              [chatId]: prev[chatId].filter(msg => msg.id !== newMessage.id)
            }));
            setInputMessage(displayText);
            ac.abort();
            return true;
          }
          case 'premium-upgrade-required': {
            let messageText = "You've reached your message limit. Upgrade to Premium for unlimited chatting!";
            let resetTimeSeconds: number | null = null;
            try {
              const parsed = JSON.parse(data);
              if (parsed.message) messageText = parsed.message;
              if (parsed.resetTimeSeconds !== undefined) {
                resetTimeSeconds = parseInt(String(parsed.resetTimeSeconds), 10);
              }
            } catch {
              if (data) messageText = data;
            }
            if (resetTimeSeconds !== null && !isNaN(resetTimeSeconds)) {
              setLimitResetTime(new Date(Date.now() + resetTimeSeconds * 1000));
            }
            setMessages(prev => ({
              ...prev,
              [chatId]: prev[chatId].map(msg =>
                msg.id === aiMessageId ? { ...msg, text: messageText } : msg
              )
            }));
            setShowLimitModal(true);
            ac.abort();
            return true;
          }
          case 'message':
          default:
            if (data === '[DONE]') {
              setMessages(prev => ({
                ...prev,
                [chatId]: prev[chatId].map(msg =>
                  msg.id === aiMessageId
                    ? { ...msg, text: aiResponseText || 'No response received.' }
                    : msg
                )
              }));
              ac.abort();
              return true;
            }
            try {
              if (appendDefaultChunk(data)) {
                ac.abort();
                return true;
              }
            } catch (e) {
              console.error('Error processing SSE data:', e);
            }
            return false;
        }
      };

      const runStream = async (isRetry: boolean): Promise<void> => {
        const token = await apiClient.ensureValidToken({ forceRefresh: isRetry });
        if (!token) {
          if (!isRetry) setAiErrorText();
          return;
        }

        const res = await fetch(streamUrl, {
          method: 'POST',
          signal: ac.signal,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            message: apiText,
            boyfriendId: chatId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          })
        });

        if (res.status === 401 && !isRetry) {
          await runStream(true);
          return;
        }

        if (!res.ok || !res.body) {
          setAiErrorText();
          return;
        }

        try {
          await consumeSseStream(res.body, handleSseEvent);
        } catch (e) {
          if ((e as Error).name === 'AbortError') return;
          console.error('SSE stream error:', e);
          setAiErrorText();
        }
      };

      await runStream(false);

    } catch (error) {
      console.error('Error getting AI response:', error);
      if (isAuthSessionExpiredError(error)) {
        return;
      }

      // Fallback to hardcoded response on error
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I'm having trouble responding right now. Please try again.",
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => ({
        ...prev,
        [selectedChat]: [...(prev[selectedChat] || []), aiResponse]
      }));
    }
  };

  const pickMediaRecorderMimeType = (): string | undefined => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined;
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mp4;codecs=mp4a.40.2',
    ];
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
    return undefined;
  };

  const extensionForRecordedMime = (mime: string): string => {
    if (mime.includes('webm')) return 'webm';
    if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
    return 'webm';
  };

  const voicePrefixForMessage = (): string => {
    const prefix = t('chat.voice_message_prefix');
    if (prefix.endsWith(':')) return `${prefix} `;
    return `${prefix}: `;
  };

  const transcribeAndSendVoiceAsText = async (file: File) => {
    if (!selectedChat) return;
    try {
      const result = await apiClient.transcribeVoiceMessage({ file, language });
      const raw = (result.text ?? '').trim();
      if (!raw) {
        toast({
          title: t('chat.voice_transcription_empty'),
          variant: 'destructive',
        });
        return;
      }
      const fullText = `${voicePrefixForMessage()}${raw}`;
      await handleSendMessage(fullText);
    } catch (error: unknown) {
      if (isAuthSessionExpiredError(error)) return;
      const msg =
        error instanceof Error && error.message
          ? error.message
          : t('chat.voice_transcription_failed');
      toast({
        title: msg,
        variant: 'destructive',
      });
    }
  };

  const stopVoiceMediaTracks = () => {
    voiceMediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    voiceMediaStreamRef.current = null;
  };

  const MIC_DISCLOSURE_STORAGE_KEY = 'mic_disclosure_accepted_v1';

  const hasAcceptedMicDisclosure = (): boolean => {
    try {
      return typeof window !== 'undefined'
        && window.localStorage.getItem(MIC_DISCLOSURE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  };

  const markMicDisclosureAccepted = () => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(MIC_DISCLOSURE_STORAGE_KEY, '1');
      }
    } catch {
      // Ignore storage errors — we'll just re-show the disclosure next time.
    }
  };

  const startVoiceRecording = async () => {
    if (!selectedChat) return;

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast({
        title: t('chat.voice_not_supported'),
        variant: 'destructive',
      });
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      toast({
        title: t('chat.voice_not_supported'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceMediaStreamRef.current = stream;
      const mimeType = pickMediaRecorderMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      voiceChunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) {
          voiceChunksRef.current.push(ev.data);
        }
      };
      recorder.onstop = async () => {
        stopVoiceMediaTracks();
        voiceMediaRecorderRef.current = null;
        const effectiveMime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(voiceChunksRef.current, { type: effectiveMime });
        voiceChunksRef.current = [];

        if (voiceRecordingUnmountRef.current) {
          return;
        }

        if (blob.size < 256) {
          setVoiceCaptureMode('idle');
          toast({
            title: t('chat.voice_too_short'),
            variant: 'destructive',
          });
          return;
        }

        setVoiceCaptureMode('uploading');
        try {
          const ext = extensionForRecordedMime(effectiveMime);
          const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: effectiveMime });
          await transcribeAndSendVoiceAsText(file);
        } finally {
          if (!voiceRecordingUnmountRef.current) {
            setVoiceCaptureMode('idle');
          }
        }
      };

      voiceMediaRecorderRef.current = recorder;
      recorder.start(250);
      setVoiceCaptureMode('recording');
    } catch (err: unknown) {
      stopVoiceMediaTracks();
      voiceMediaRecorderRef.current = null;
      setVoiceCaptureMode('idle');
      const name = err instanceof DOMException ? err.name : (err as Error)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        toast({
          title: t('chat.voice_mic_access_denied'),
          variant: 'destructive',
        });
        return;
      }
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        toast({
          title: t('chat.voice_mic_not_found'),
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: t('chat.voice_recording_failed'),
        variant: 'destructive',
      });
    }
  };

  const handleVoiceMicClick = async () => {
    if (!selectedChat || voiceCaptureMode === 'uploading') return;

    if (!user?.isPremium && voiceCaptureMode === 'idle') {
      setShowVoicePremiumModal(true);
      return;
    }

    if (voiceCaptureMode === 'recording') {
      const recorder = voiceMediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        stopVoiceMediaTracks();
        setVoiceCaptureMode('idle');
      }
      return;
    }

    // Prominent disclosure (Google Play policy): show our own dialog explaining
    // why/when we record BEFORE the system permission prompt appears. Only the
    // first time — after the user accepts we remember the choice locally.
    if (!hasAcceptedMicDisclosure()) {
      setShowMicDisclosure(true);
      return;
    }

    await startVoiceRecording();
  };

  const handleMicDisclosureAccept = async () => {
    markMicDisclosureAccepted();
    setShowMicDisclosure(false);
    await startVoiceRecording();
  };

  const handleMicDisclosureCancel = () => {
    setShowMicDisclosure(false);
  };

  const handleAiDisclosureConfirm = async () => {
    await setAiDisclosureAccepted();
    setShowAiDisclosure(false);
  };


  const handleToggleFavoriteLocal = async (contactId: string) => {
    setLocalFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(contactId)) {
        newFavorites.delete(contactId);
      } else {
        newFavorites.add(contactId);
      }
      return newFavorites;
    });
    
    // Also call the parent handler
    await onToggleFavorite(contactId);
  };

  const handlePremiumUpgrade = () => {
    setShowUpgradeModal(false);
    setShowPaymentModal(true);
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: 'card' | 'crypto') => {
    // Silent subscription activation - no notification
    setShowPaymentModal(false);
  };

  // Load tokens when generate modal opens
  useEffect(() => {
    if (showGenerateModal && user?.id) {
      const loadTokens = async () => {
        try {
          const userData = await apiClient.get('/users/me');
          const freshTokens = userData?.currentTokens || 0;
          setModalTokens(freshTokens);
        } catch (error) {
          console.error('Error fetching user tokens:', error);
          setModalTokens(0);
        }
      };
      loadTokens();
    }
  }, [showGenerateModal, user?.id]);

  const handleGenerate = async () => {
    if (!user) {
      onPromptLogin();
      return;
    }
    if (!selectedChat) return;

    if (!generatePrompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please describe your vision before generating.",
        variant: "destructive"
      });
      return;
    }

    setShowGenerationDialog(true);
    setGeneratedImageUrl(null);
    setIsGenerating(true);
    setShowGenerateModal(false);

    try {
      const res = await apiClient.generateImage({ userPrompt: generatePrompt, boyfriendId: selectedChat, userPhotoBase64: uploadedUserPhoto });
      const imageUrl = res?.imageUrl || null;
      if (!imageUrl) {
        toast({ title: "No image returned", description: "The server did not return an image URL.", variant: "destructive" });
        setIsGenerating(false);
        setShowGenerationDialog(false);
        return;
      }
      setGeneratedImageUrl(imageUrl);
      setGeneratedImageId(res?.imageId || null);
      setIsGenerating(false);
      setGeneratePrompt('');
      toast({ title: "Image Generated!", description: "Your custom image has been created." });
    } catch (error: any) {
      if (error?.status === 400) {
        setIsGenerating(false);
        setShowGenerationDialog(false);
        onTokensClick?.();
        return;
      }
      console.error('Error generating image:', error);
      toast({
        title: "Generation Failed",
        description: error?.message || "An unexpected error occurred.",
        variant: "destructive"
      });
      setIsGenerating(false);
      setShowGenerationDialog(false);
    }
  };

  const suggestions = selectedContact ? [
    t('chat.suggestion_tell_me_day'),
    t('chat.suggestion_favorite_memory'),
    t('chat.suggestion_missed_you'),
    t('chat.suggestion_thinking_about')
  ] : [];

  // Load boyfriend data on mount
  useEffect(() => {
    loadBoyfriendData();
    // Reset pagination state when switching chats
    setOldestMessageId(null);
    setHasMoreMessages(true);
    setIsLoadingMore(false);
  }, [user?.id, selectedBoyfriendId]);

  // With column-reverse, scroll is automatically at the bottom (scrollTop = 0)
  // No need for manual scrolling - new messages automatically appear at the bottom

  // Set selectedChat when selectedBoyfriendId changes
  useEffect(() => {
    if (selectedBoyfriendId) {
      setSelectedChat(selectedBoyfriendId);
    }
  }, [selectedBoyfriendId]);

  useEffect(() => {
    voiceRecordingUnmountRef.current = false;
    return () => {
      voiceRecordingUnmountRef.current = true;
      chatStreamAbortRef.current?.abort();
      const recorder = voiceMediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
      }
      voiceMediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      voiceMediaStreamRef.current = null;
    };
  }, []);

  // When nothing is selected, navigate back to list outside of render
  // BUT NOT if we're in error state or still loading
  useEffect(() => {
    if ((!selectedChat || !selectedContact) && !apiError && !isLoading) {
      console.log('Navigating back to list - selectedChat:', selectedChat, 'selectedContact:', selectedContact, 'isLoading:', isLoading);
      onBackToList();
    }
  }, [selectedChat, selectedContact, onBackToList, apiError, isLoading]);

  // Control bottom navigation visibility - hide when in conversation on mobile
  useEffect(() => {
    if (onBottomNavVisibilityChange && isMobile) {
      onBottomNavVisibilityChange(false);
    }
    
    // Cleanup: restore nav visibility when component unmounts
    return () => {
      if (onBottomNavVisibilityChange && isMobile) {
        onBottomNavVisibilityChange(true);
      }
    };
  }, [isMobile, onBottomNavVisibilityChange]);

  const isLoggedIn = !!user;

  if (!isLoggedIn) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">Sign in to start chatting</h2>
            <p className="text-muted-foreground mb-6">
              Connect with your AI boyfriends and enjoy personalized conversations
            </p>
            <Button variant="romantic" onClick={onPromptLogin}>
              <User className="h-4 w-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - Skeleton chat UI
  if (isLoading) {
    return (
      <div className="bg-gradient-dreamy h-full overflow-hidden flex flex-col">
        {/* Skeleton Header */}
        <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isMobile && <Skeleton className="h-8 w-8 rounded-md" />}
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-14" />
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </div>
        </div>
        {/* Skeleton Messages */}
        <div className="flex-1 px-4 py-3 space-y-3 overflow-hidden">
          <div className="flex justify-start">
            <Skeleton className="h-16 w-[65%] rounded-2xl" />
          </div>
          <div className="flex justify-end">
            <Skeleton className="h-10 w-[45%] rounded-2xl" />
          </div>
          <div className="flex justify-start">
            <Skeleton className="h-12 w-[55%] rounded-2xl" />
          </div>
          <div className="flex justify-end">
            <Skeleton className="h-10 w-[40%] rounded-2xl" />
          </div>
          <div className="flex justify-start">
            <Skeleton className="h-20 w-[70%] rounded-2xl" />
          </div>
        </div>
        {/* Skeleton Input */}
        <div className="px-4 py-3 pb-4">
          <div className="flex gap-2">
            <Skeleton className="h-9 w-9 rounded-md shrink-0" />
            <Skeleton className="h-9 flex-1 rounded-md" />
            <Skeleton className="h-9 w-9 rounded-md shrink-0" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (apiError) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">Error loading chats</h2>
            <p className="text-muted-foreground mb-6">{apiError}</p>
            <Button variant="romantic" onClick={loadBoyfriendData}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If no chat is selected, render nothing (navigation handled in effect)
  // BUT NOT if we're in error state - show error instead
  if ((!selectedChat || !selectedContact) && !apiError) {
    return null;
  }

  return (
    <div className="bg-gradient-dreamy h-full overflow-hidden flex flex-col">
      {/* Main Chat Content */}
      <div className="flex-1 flex min-h-0">
        {/* Chat Area */}
          <div className={`flex-1 flex flex-col relative min-h-0 ${isMobile ? 'w-full' : 'min-w-0 overflow-hidden max-w-full'}`}>
            {selectedChat && selectedContact ? (
              <>
                {/* Blurred avatar background */}
                {selectedContact.avatarUrl && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${selectedContact.avatarUrl})`,
                      filter: 'blur(4px) brightness(0.5)',
                      transform: 'scale(1.04)',
                    }}
                  />
                )}
                {/* Chat Header */}
                <div className="relative z-10 bg-card/40 backdrop-blur-md border-b border-primary/20 p-4 shrink-0" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                       {/* Mobile back button */}
                       {isMobile && (
                          <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                            onClick={onBackToList}
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <div className="relative shrink-0">
                        <Avatar 
                          className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} border-2 border-primary/20 cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all`}
                          onClick={() => onNavigateToProfilePage(selectedContact.id)}
                        >
                          <AvatarImage 
                            src={selectedContact.avatarUrl} 
                            alt={selectedContact.name} 
                          />
                          <AvatarFallback>{selectedContact.name[0]}</AvatarFallback>
                        </Avatar>
                        {/* Always show online indicator */}
                        <div className={`absolute -bottom-0.5 -right-0.5 ${isMobile ? 'w-2.5 h-2.5' : 'w-4 h-4'} bg-green-500 rounded-full border border-background`}></div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 
                          className={`font-semibold truncate cursor-pointer hover:text-primary transition-colors ${isMobile ? 'text-sm' : ''}`}
                          onClick={() => onNavigateToProfilePage(selectedContact.id)}
                        >
                          {selectedContact.name}
                        </h3>
                        <p className={`text-muted-foreground truncate flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {t('chat.online')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`transition-all duration-300 hover:scale-110 ${isMobile ? 'h-8 w-8' : ''}`}
                        onClick={async () => {
                          if (!selectedContact) return;
                          try {
                            setGalleryLoading(true);
                            const list = await apiClient.getUserGeneratedImagesByBoyfriend(selectedContact.id);
                            setGalleryImages(Array.isArray(list) ? list.filter((it: any) => it?.id && it?.imageUrl).map((it: any) => ({ id: it.id, url: it.imageUrl })) : []);
                            setShowGalleryModal(true);
                          } catch (e) {
                            toast({ title: 'Failed to load gallery', variant: 'destructive' });
                          } finally {
                            setGalleryLoading(false);
                          }
                        }}
                      >
                        <Camera className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`transition-all duration-300 hover:scale-110 ${isMobile ? 'h-8 w-8' : ''} ${
                          favorites.has(selectedContact.id) 
                            ? 'text-red-500 hover:text-red-600 bg-red-500/10 hover:bg-red-500/20 ring-2 ring-red-500/30 shadow-lg shadow-red-500/25' 
                            : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/10 hover:shadow-lg hover:shadow-red-500/25'
                        }`}
                        onClick={() => handleToggleFavoriteLocal(selectedContact.id)}
                      >
                        <Heart className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} transition-all duration-300 ${
                          favorites.has(selectedContact.id) 
                            ? 'fill-current scale-110 drop-shadow-lg' 
                            : 'fill-none hover:fill-current/30'
                        }`} />
                      </Button>
                      {!isMobile && (
                        <Button variant="ghost" size="icon">
                          <Settings className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages Area */}
                <div
                  ref={messagesContainerRef}
                  className={`chat-messages-container relative z-10 flex flex-col-reverse flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isMobile ? 'px-2 py-2 pb-36' : 'px-4 py-3 pb-40'}`}
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'hsl(var(--primary) / 0.3) hsl(var(--muted))',
                    ...(inputBarHeight > 0 ? { paddingBottom: inputBarHeight + 8 } : {}),
                  }}
                  onScroll={(e) => {
                    const element = e.target as HTMLDivElement;
                    // Load more when scrolled to bottom (which is scrollHeight with column-reverse)
                    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
                    if (isAtBottom && hasMoreMessages && !isLoadingMore) {
                      loadMoreMessages();
                    }
                  }}
                >
                  {(messages[selectedChat] || []).length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-full text-center px-4 py-12">
                      <MessageCircle className="h-16 w-16 text-muted-foreground/50 mb-4" />
                      <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                        You have no conversation started yet
                      </h3>
                      <p className="text-sm text-muted-foreground/70 max-w-sm">
                        Start chatting with {selectedContact?.name || 'your boyfriend'} to begin your conversation!
                      </p>
                    </div>
                  ) : (() => {
                    const thread = messages[selectedChat] || [];
                    const newestAiMsgIndex = thread.findIndex(
                      (m) =>
                        m.sender === 'ai' &&
                        !m.isReported &&
                        m.text !== 'LOADING_DOTS' &&
                        (Boolean(m.text) || Boolean(m.imageUrl))
                    );
                    return thread.map((message, index) => {
                      const showMessageCoach =
                        coachPrefsLoaded &&
                        !menuCoachDismissed &&
                        newestAiMsgIndex !== -1 &&
                        index === newestAiMsgIndex &&
                        message.sender === 'ai' &&
                        !message.isReported;

                      return (
                    <div
                      key={message.id}
                      className={`flex flex-col mb-2 ${message.sender === 'user' ? 'items-end' : 'items-start'}`}
                    >
                      {showMessageCoach && (
                        <button
                          type="button"
                          className={`mb-1.5 text-left ${isMobile ? 'max-w-[85%]' : 'max-w-[75%]'} rounded-xl border-2 border-primary/60 bg-card px-3 py-2.5 text-xs leading-snug text-foreground shadow-md cursor-pointer transition-colors hover:bg-muted dark:bg-zinc-900 dark:border-primary/70 dark:hover:bg-zinc-800`}
                          role="status"
                          onClick={() => void persistCoachDismissal()}
                        >
                          {t('chat.coach_hold_message')}
                        </button>
                      )}
                    <div className={`flex w-full ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                       <div
                         {...messageBubblePointerHandlers(message)}
                         className={`touch-manipulation ${isMobile ? 'max-w-[85%]' : 'max-w-[75%]'} ${
                           message.sender === 'user'
                             ? 'bg-primary text-primary-foreground'
                             : 'bg-gray-200 dark:bg-gray-700'
                         } ${message.imageUrl ? 'p-1' : 'px-3 py-2'} rounded-2xl`}
                         style={{ 
                           wordBreak: 'break-word',
                           overflowWrap: 'break-word',
                           hyphens: 'auto'
                         }}
                       >
                         {message.imageUrl && (
                           <div className="mb-2">
                            <img
                              src={message.imageUrl}
                              alt="Shared photo"
                              className="w-full h-auto rounded-lg max-w-[250px] cursor-zoom-in"
                              onClick={() => { setActiveImageUrl(message.imageUrl!); setShowImageModal(true); }}
                            />
                           </div>
                         )}
                          {message.text && (
                            <div className={`${message.imageUrl ? 'px-2 pb-2' : ''} relative`}>
                              {message.text === 'LOADING_DOTS' ? (
                                <div className="flex items-center pr-14">
                                  <LoadingDots />
                                  <span className="invisible">&nbsp;</span>
                                </div>
                              ) : (
                                <p className={`${isMobile ? 'text-sm' : 'text-base'} leading-relaxed whitespace-pre-wrap pr-14`}>
                                  {(() => {
                                    // Remove command from beginning if present (for user messages loaded from API)
                                    let displayText = message.text;
                                    if (message.sender === 'user') {
                                      const commandMatch = displayText.match(/^#\[([A-Z_]+)\]#\s*/);
                                      if (commandMatch) {
                                        displayText = displayText.substring(commandMatch[0].length);
                                      }
                                    }
                                    return displayText;
                                  })()}
                                </p>
                              )}
                              {message.sender === 'ai' && message.actions && message.actions.length > 0 && (
                                <div className="mt-2 pr-14 flex flex-wrap items-center gap-2">
                                  {message.actions.map((a, i) => (
                                    <Button
                                      key={i}
                                      type="button"
                                      variant={a.style === 'button' ? 'secondary' : 'link'}
                                      size="sm"
                                      className={a.style === 'button' ? 'h-8' : 'h-auto p-0 text-primary underline font-normal'}
                                      onClick={() => runChatMessageAction(a.type)}
                                    >
                                      {a.label || a.type}
                                    </Button>
                                  ))}
                                </div>
                              )}
                              <span className={`absolute bottom-0 right-0 text-xs ${
                                message.sender === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}>
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                          {!message.text && (
                            <div className={`${message.imageUrl ? 'px-2 pb-1' : ''} flex justify-end`}>
                              <p className={`text-xs ${message.imageUrl ? '' : 'mt-1'} ${
                                message.sender === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}>
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          )}
                       </div>
                    </div>
                    </div>
                      );
                    });
                  })()}
                  <div ref={messagesEndRef} />
                  {/* Load More - last in DOM = top with flex-col-reverse (above oldest messages) */}
                  {hasMoreMessages && (messages[selectedChat] || []).length > 0 && (
                    <div className="flex justify-center py-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={loadMoreMessages}
                        disabled={isLoadingMore}
                        className="text-xs"
                      >
                        {isLoadingMore ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-primary mr-2"></div>
                            Loading...
                          </>
                        ) : (
                          'Load older messages'
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {messageContextMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-[200]"
                      aria-hidden
                      onPointerDown={() => setMessageContextMenu(null)}
                    />
                    <div
                      role="menu"
                      className="fixed z-[210] min-w-[188px] rounded-md border bg-popover text-popover-foreground shadow-lg py-1"
                      style={{
                        left: messageContextMenu.x,
                        top: messageContextMenu.y,
                        transform: 'translate(-50%, calc(-100% - 10px))',
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full justify-start gap-2 rounded-none px-3 h-9 font-normal"
                        disabled={!getDisplayTextForCopy(messageContextMenu.message)}
                        onClick={async () => {
                          const txt = getDisplayTextForCopy(messageContextMenu.message);
                          try {
                            await navigator.clipboard.writeText(txt);
                            toast({ title: t('chat.message_copied') });
                          } catch {
                            toast({ title: 'Could not copy', variant: 'destructive' });
                          }
                          setMessageContextMenu(null);
                        }}
                      >
                        <Copy className="h-4 w-4 shrink-0 opacity-70" />
                        {t('chat.message_menu_copy')}
                      </Button>
                      {messageContextMenu.message.messageExchangeId &&
                        messageContextMenu.message.sender === 'ai' &&
                        !messageContextMenu.message.isReported && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full justify-start gap-2 rounded-none px-3 h-9 font-normal"
                          onClick={() => {
                            setReportPendingExchangeId(messageContextMenu.message.messageExchangeId!);
                            setReportReason('SPAM');
                            setReportDialogOpen(true);
                            setMessageContextMenu(null);
                          }}
                        >
                          <Flag className="h-4 w-4 shrink-0 opacity-70" />
                          {t('chat.report_message')}
                        </Button>
                      )}
                      {messageContextMenu.message.imageUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full justify-start gap-2 rounded-none px-3 h-9 font-normal"
                          onClick={async () => {
                            const url = messageContextMenu!.message.imageUrl!;
                            try {
                              await navigator.clipboard.writeText(url);
                              toast({ title: t('chat.message_copied') });
                            } catch {
                              toast({ title: 'Could not copy', variant: 'destructive' });
                            }
                            setMessageContextMenu(null);
                          }}
                        >
                          <Copy className="h-4 w-4 shrink-0 opacity-70" />
                          {t('chat.message_menu_copy_image')}
                        </Button>
                      )}
                      {messageContextMenu.message.messageExchangeId &&
                        (isDebug || debugChatPromptsEnabled) &&
                        !messageContextMenu.message.isReported && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full justify-start gap-2 rounded-none px-3 h-9 font-normal"
                          onClick={() => {
                            handleDebugPrompt(messageContextMenu!.message.id);
                            setMessageContextMenu(null);
                          }}
                        >
                          <Bug className="h-4 w-4 shrink-0 opacity-70" />
                          {t('chat.message_menu_debug')}
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {/* Message Input */}
                <div
                  ref={inputBarRef}
                  className={`${isMobile ? 'fixed' : 'absolute'} bottom-0 left-0 right-0 bg-card/40 backdrop-blur-md px-4 py-3 pb-4 shrink-0 z-10`}
                  style={{ paddingBottom: "calc(1rem + var(--safe-area-inset-bottom, 0px))" }}
                >
                  {/* Message Suggestions */}
                  {suggestions.length > 0 && (
                    <div className="pb-3 mb-3 border-b border-primary/20 -mx-4 pl-4">
                      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                        {/* Priority suggestions */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-auto py-1.5 px-2 border border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 backdrop-blur-sm shrink-0 flex items-center gap-1"
                          onClick={() => {
                            handleSendMessage(t('chat.suggestion_send_photo'));
                          }}
                        >
                          <Camera className="h-3 w-3" />
                          {t('chat.photo')}
                        </Button>
                        {suggestions.map((suggestion, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className="text-xs h-auto py-1.5 px-2 border border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 backdrop-blur-sm shrink-0"
                            onClick={() => {
                              handleSendMessage(suggestion);
                            }}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <div className="relative shrink-0">
                      <Button
                        type="button"
                        variant={voiceCaptureMode === 'recording' ? 'destructive' : 'outline'}
                        size="icon"
                        className={`shrink-0 ${isMobile ? 'h-9 w-9' : ''}`}
                        disabled={voiceCaptureMode === 'uploading'}
                        aria-pressed={voiceCaptureMode === 'recording'}
                        title={
                          voiceCaptureMode === 'recording'
                            ? t('chat.voice_tap_to_stop')
                            : t('chat.voice_tap_to_record')
                        }
                        onClick={() => void handleVoiceMicClick()}
                      >
                        {voiceCaptureMode === 'recording' ? (
                          <Square className="h-4 w-4 fill-current" />
                        ) : (
                          <Mic className="h-4 w-4" />
                        )}
                      </Button>
                      {!user?.isPremium && voiceCaptureMode !== 'recording' && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute -top-1 -right-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm ring-1 ring-amber-200/60"
                        >
                          <Crown className="h-2.5 w-2.5 text-white" />
                        </span>
                      )}
                    </div>
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      maxLength={maxMessageLength}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(inputMessage);
                        }
                      }}
                      placeholder={`${t('chat.type_message')} ${selectedContact.name}...`}
                      className={`flex-1 bg-muted border-primary/20 focus:border-primary placeholder:text-muted-foreground/60 ${isMobile ? 'text-sm' : ''}`}
                    />
                    <Button
                      onClick={() => handleSendMessage(inputMessage)}
                      disabled={!inputMessage.trim()}
                      variant="romantic"
                      size="icon"
                      className={`shrink-0 ${isMobile ? 'h-9 w-9' : ''}`}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* No Chat Selected */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-md">
                  {chatContacts.length === 0 ? (
                    <>
                      <div className="bg-card/50 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                        <Plus className="h-12 w-12 text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-semibold mb-4">No conversations yet</h3>
                      <p className="text-muted-foreground mb-6">
                        Create your first AI boyfriend to start chatting
                      </p>
                      {isLoggedIn ? (
                        <Button variant="romantic" onClick={onNavigateToCharacterCreation}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Your First Boyfriend
                        </Button>
                      ) : (
                        <Button variant="romantic" onClick={onPromptLogin}>
                          <User className="h-4 w-4 mr-2" />
                          Sign In to Get Started
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">Select a conversation</h3>
                      <p className="text-muted-foreground">
                        Choose a conversation from the sidebar to start chatting
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Section - Show only on desktop when contact is selected */}
          {!isMobile && selectedContact && (
            <div className="w-80 bg-card/60 backdrop-blur-sm border-l border-primary/20 flex flex-col">
              {/* Profile Header */}
              <div className="relative flex-1">
                <div className="h-full relative">
                  <img
                    src={selectedContact.avatarUrl}
                    alt={selectedContact.name}
                    className="w-full h-full object-cover"
                  />
                  {selectedContact.isOnline && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-green-500 rounded-full border-4 border-white shadow-lg"></div>
                  )}
                  
                  {/* Profile overlay info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                    <h2 className="text-2xl font-bold text-white mb-2">{selectedContact.name}</h2>
                    <p className="text-white/90 text-sm mb-3">{selectedContact.bio}</p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-white/80 text-sm">
                        <Calendar className="h-4 w-4" />
                        <span>{selectedContact.age} years old</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/80 text-sm">
                        <MapPin className="h-4 w-4" />
                        <span>{selectedContact.location}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mt-3">
                      {selectedContact.interests.map((interest, index) => (
                        <Badge key={index} variant="secondary" className="text-xs bg-white/20 text-white border-white/30">
                          {t(interest)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Actions */}
              <div className="p-4 border-t border-primary/20 space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowPrivateContentModal(true)}
                >
                  <FileImage className="h-4 w-4 mr-2" />
                  View Photos
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={async () => {
                    if (!selectedContact) return;
                    try {
                      setGalleryLoading(true);
                      const list = await apiClient.getUserGeneratedImagesByBoyfriend(selectedContact.id);
                      setGalleryImages(Array.isArray(list) ? list.filter((it: any) => it?.id && it?.imageUrl).map((it: any) => ({ id: it.id, url: it.imageUrl })) : []);
                      setShowGalleryModal(true);
                    } catch (e) {
                      toast({ title: 'Failed to load gallery', variant: 'destructive' });
                    } finally {
                      setGalleryLoading(false);
                    }
                  }}
                >
                  <FileImage className="h-4 w-4 mr-2" />
                  {t('chat.gallery.title')}
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start"
                >
                  <Star className="h-4 w-4 mr-2" />
                  Rate Conversation
                </Button>
              </div>
            </div>
          )}
        </div>

      {/* Token Balance Modal */}
      <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-500" />
              {t('tokens.modal_title')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center p-6 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-lg border border-amber-500/20">
              <Coins className="h-12 w-12 mx-auto mb-3 text-amber-500" />
              <div className="text-3xl font-bold text-amber-600">{userTokens}</div>
              <div className="text-sm text-muted-foreground">{t('tokens.chat_available_label')}</div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-card/50 rounded-lg">
                <span className="text-sm">{t('tokens.cost_send_message')}</span>
                <span className="text-sm font-medium">{t('tokens.cost_one_token')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card/50 rounded-lg">
                <span className="text-sm">{t('tokens.cost_request_photo')}</span>
                <span className="text-sm font-medium">{t('tokens.cost_n_tokens', { count: 5 })}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card/50 rounded-lg">
                <span className="text-sm">{t('tokens.cost_voice_message')}</span>
                <span className="text-sm font-medium">{t('tokens.cost_n_tokens', { count: 3 })}</span>
              </div>
            </div>
            
            <Button
              variant="romantic"
              className="w-full"
              onClick={() => {
                setShowTokenModal(false);
                setShowTokenPaymentModal(true);
              }}
            >
              <Zap className="h-4 w-4 mr-2" />
              {t('tokens.purchase_more_cta')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Crown className="h-6 w-6 text-amber-500" />
              Upgrade to Premium
            </DialogTitle>
            <DialogDescription>
              Unlock unlimited conversations and exclusive features
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'monthly' ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
            }`} onClick={() => setSelectedPlan('monthly')}>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Monthly Plan</h3>
                <div className="text-3xl font-bold text-primary mb-4">$9.99</div>
                <p className="text-sm text-muted-foreground mb-6">per month</p>
                
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Unlimited messages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Premium content access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Priority support</span>
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'yearly' ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
            }`} onClick={() => setSelectedPlan('yearly')}>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Yearly Plan</h3>
                <div className="text-3xl font-bold text-primary mb-1">$99.99</div>
                <div className="text-sm text-green-600 font-medium mb-3">Save 17%</div>
                <p className="text-sm text-muted-foreground mb-6">per year</p>
                
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Everything in Monthly</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Exclusive yearly bonuses</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="space-y-4">
            <Button 
              onClick={handlePremiumUpgrade}
              variant="romantic" 
              size="lg" 
              className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to {selectedPlan === 'monthly' ? 'Monthly' : 'Yearly'} Plan
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Secure payment • Cancel anytime
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Private Content Modal */}
      {selectedContact && (
        <Dialog open={showPrivateContentModal} onOpenChange={setShowPrivateContentModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pr-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-red-500" />
                  <DialogTitle>{selectedContact.name}'s Private Content</DialogTitle>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-lg border border-amber-500/20">
                    <Coins className="h-4 w-4 text-amber-500" />
                    <span className="font-semibold">{userTokens}</span>
                    <span className="text-muted-foreground text-sm">tokens</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setShowPrivateContentModal(false);
                      setShowTokenModal(true);
                    }}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Buy Tokens
                  </Button>
                </div>
              </div>
            </DialogHeader>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="relative group">
                  <div className="aspect-square bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-lg flex items-center justify-center border border-primary/20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <Lock className="h-8 w-8 mx-auto mb-2 text-white" />
                        <p className="text-white text-sm font-medium">5 tokens</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="romantic" 
                    size="sm" 
                    className="w-full mt-2"
                    disabled={userTokens < 5}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Unlock (5 tokens)
                  </Button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Gallery Modal */}
      {selectedContact && (
        <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedContact.name} – {t('chat.gallery.title')}</DialogTitle>
              <DialogDescription>
                {t('chat.gallery.description')}
              </DialogDescription>
            </DialogHeader>
            {galleryLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            ) : galleryImages.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">{t('chat.gallery.no_photos')}</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {galleryImages.map((image, idx) => (
                  <div key={image.id} className="relative rounded-lg overflow-hidden border border-primary/20">
                    <img
                      src={image.url}
                      alt={`Generated ${idx}`}
                      className="w-full h-auto object-cover cursor-zoom-in"
                      onClick={() => { setActiveImageUrl(image.url); setShowImageModal(true); }}
                    />
                    <button
                      type="button"
                      onClick={() => setReportImageId(image.id)}
                      className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
                      aria-label={t('image.report')}
                    >
                      <Flag className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="romantic"
              className="w-full mt-2"
              onClick={() => {
                setShowGalleryModal(false);
                setShowGenerateModal(true);
              }}
            >
              <Camera className="h-4 w-4 mr-2" />
              {t('chat.gallery.generate_photos')}
            </Button>
          </DialogContent>
        </Dialog>
      )}

      <ReportImageDialog
        imageId={reportImageId}
        onClose={() => setReportImageId(null)}
        onReported={(imageId) => {
          setGalleryImages(prev => prev.filter(image => image.id !== imageId));
          if (imageId === generatedImageId) {
            setShowGenerationDialog(false);
            setGeneratedImageUrl(null);
            setGeneratedImageId(null);
          }
        }}
      />

      {/* Image Zoom Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Image preview</DialogTitle>
          </DialogHeader>
          <div className="w-full h-full flex items-center justify-center">
            {activeImageUrl && (
              <img src={activeImageUrl} alt="Zoomed" className="max-h-[80vh] w-auto h-auto rounded-lg" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <SubscriptionPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        selectedPlan={selectedPlan}
        onPurchaseSubscription={handlePurchaseSubscription}
      />

      <Dialog
        open={reportDialogOpen}
        onOpenChange={(open) => {
          setReportDialogOpen(open);
          if (!open) setReportPendingExchangeId(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('chat.report_title')}</DialogTitle>
            <DialogDescription>{t('chat.report_description')}</DialogDescription>
          </DialogHeader>
          <RadioGroup value={reportReason} onValueChange={setReportReason} className="grid gap-3 py-2">
            {(
              [
                ['SPAM', 'chat.report_reason_spam'],
                ['HARASSMENT', 'chat.report_reason_harassment'],
                ['HATE', 'chat.report_reason_hate'],
                ['SEXUAL_CONTENT', 'chat.report_reason_sexual'],
                ['OTHER', 'chat.report_reason_other'],
              ] as const
            ).map(([value, key]) => (
              <div key={value} className="flex items-center space-x-2">
                <RadioGroupItem value={value} id={`report-${value}`} />
                <Label htmlFor={`report-${value}`} className="font-normal cursor-pointer">
                  {t(key)}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setReportDialogOpen(false);
                setReportPendingExchangeId(null);
              }}
            >
              {t('chat.report_cancel')}
            </Button>
            <Button type="button" variant="romantic" disabled={reportSubmitting} onClick={() => void submitChatReport()}>
              {reportSubmitting ? '…' : t('chat.report_submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Chat?</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this chat? This action cannot be undone and all messages will be lost.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setChatToDelete(null);
                setSwipedChatId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => {
                if (chatToDelete) {
                  // Handle actual delete logic here
                  console.log('Permanently deleting chat:', chatToDelete);
                  // You can add the actual deletion logic here
                }
                setDeleteConfirmOpen(false);
                setChatToDelete(null);
                setSwipedChatId(null);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Content Modal */}
      {selectedContact && (
        <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
          <DialogContent
            className="w-screen h-screen max-w-none m-0 p-0 rounded-none overflow-y-auto"
            onOpenAutoFocus={(e) => e.preventDefault()}
            hideCloseButton
          >
            <div className="sticky top-0 z-20 bg-background bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/20 p-6 border-b border-primary/20" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-purple-500/10" />
              <div className="relative z-10 mb-3">
                <div className="flex items-center justify-center relative">
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="absolute left-0 p-2 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                  <DialogTitle className="px-10 text-2xl md:text-3xl font-bold bg-gradient-romantic bg-clip-text text-transparent text-center">
                    {t('boyfriend_profile.generate_custom_content')}
                  </DialogTitle>
                </div>
              </div>
              <DialogDescription className="relative z-10 text-center text-muted-foreground text-base md:text-lg">
                {t('boyfriend_profile.create_personalized')} {selectedContact.name}
              </DialogDescription>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <Card className="overflow-hidden border-2 border-primary/20">
                  <div className="relative">
                    <img src={sampleCoupleHug} alt="Sample romantic couple" className="w-full h-64 md:h-80 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h4 className="font-bold text-lg mb-1">{t('boyfriend_profile.romantic_moments')}</h4>
                      <p className="text-sm opacity-90">{t('boyfriend_profile.create_intimate')} {selectedContact.name}</p>
                    </div>
                    <div className="absolute top-4 right-4">
                      <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                        {t('boyfriend_profile.sample')}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{t('boyfriend_profile.describe_vision')}</h3>
                <Textarea
                  placeholder={t('boyfriend_profile.vision_placeholder').replace('{{name}}', selectedContact.name || '')}
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  rows={4}
                  autoFocus={false}
                  className="border-2 border-primary/20 focus:border-primary/40 resize-none placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{t('boyfriend_profile.upload_photo')}</h3>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setUploadedUserPhoto(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  />
                  {uploadedUserPhoto && (
                    <div className="relative w-32 h-32 border-2 border-primary/20 rounded-lg overflow-hidden">
                      <img src={uploadedUserPhoto} alt="Uploaded preview" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setUploadedUserPhoto('')}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-primary/20">
                <div className="flex items-center gap-2 order-2 md:order-1">
                  <Coins className="h-5 w-5 text-amber-500" />
                  <span className="font-semibold">{modalTokens} {t('boyfriend_profile.tokens_available')}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowGenerateModal(false);
                      onTokensClick?.();
                    }}
                    className="ml-1 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('tokens.purchase_more_cta')}
                  </Button>
                </div>
                <div className="flex gap-3 order-1 md:order-2 w-full md:w-auto">
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating || !generatePrompt.trim()}
                    variant="romantic"
                    className="flex-1 md:flex-none bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 hover:shadow-lg transition-all duration-300"
                  >
                    {isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('boyfriend_profile.generating')}
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        {t('boyfriend_profile.generate')} (20 {t('boyfriend_profile.tokens')})
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Generation Progress Dialog */}
      {selectedContact && (
        <Dialog open={showGenerationDialog} onOpenChange={setShowGenerationDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl">
                {isGenerating ? t('boyfriend_profile.generating_image') : t('boyfriend_profile.image_generated')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-6">
              {isGenerating ? (
                <div className="flex flex-col items-center justify-center space-y-6">
                  <div className="w-full max-w-md space-y-3">
                    <Progress value={undefined} className="h-2" />
                    <p className="text-center text-muted-foreground">{t('boyfriend_profile.creating_custom')}</p>
                  </div>
                  <div className="animate-pulse text-sm text-muted-foreground">{t('boyfriend_profile.take_moments')}</div>
                </div>
              ) : generatedImageUrl ? (
                <div className="space-y-4">
                  <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg bg-muted">
                    <img src={generatedImageUrl} alt="Generated image" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex gap-3 justify-center">
                    {generatedImageId && (
                      <Button
                        variant="outline"
                        onClick={() => setReportImageId(generatedImageId)}
                        className="h-10"
                      >
                        <Flag className="h-4 w-4 mr-2" />
                        {t('image.report')}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => { setShowGenerationDialog(false); setGeneratedImageUrl(null); }}
                      className="h-10"
                    >
                      {t('boyfriend_profile.close')}
                    </Button>
                    <Button
                      onClick={() => {
                        if (generatedImageUrl) {
                          const link = document.createElement('a');
                          link.href = generatedImageUrl;
                          link.download = `generated-${selectedContact?.name || 'image'}-${Date.now()}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }
                      }}
                      className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 h-10"
                    >
                      {t('boyfriend_profile.save_image')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Message Limit Modal - only show if resetTime is available */}
      {limitResetTime && (
        <MessageLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          onUpgrade={onNavigateToPremium}
          resetTime={limitResetTime}
        />
      )}

      <PremiumFeatureModal
        isOpen={showVoicePremiumModal}
        onClose={() => setShowVoicePremiumModal(false)}
        title={t('chat.voice_premium_title')}
        subtitle={t('chat.voice_premium_subtitle')}
        description={t('chat.voice_premium_desc')}
        icon={<Mic className="h-12 w-12" />}
        user={user}
        onPromptLogin={onPromptLogin}
        onUpgradeToPremium={onNavigateToPremium}
      />

      <AiDisclosureModal
        isOpen={showAiDisclosure}
        onConfirm={handleAiDisclosureConfirm}
      />

      <MicDisclosureModal
        isOpen={showMicDisclosure}
        onAccept={handleMicDisclosureAccept}
        onCancel={handleMicDisclosureCancel}
      />
    </div>
  );
};

// Swipeable Chat Component
interface SwipeableChatProps {
  contact: any;
  isSelected: boolean;
  onSelect: () => void;
  onSwipe: (id: string) => void;
  onDelete: (id: string) => void;
  localFavorites: Set<string>;
  userStyle: 'anime' | 'realistic';
}

const SwipeableChat: React.FC<SwipeableChatProps> = ({
  contact,
  isSelected,
  onSelect,
  onSwipe,
  onDelete,
  localFavorites,
  userStyle
}) => {
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setCurrentX(0);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const touchX = e.touches[0].clientX;
    const diffX = startX - touchX;
    
    // Only allow left swipe (positive diffX)
    if (diffX > 0) {
      setCurrentX(Math.min(diffX, 80)); // Max swipe distance of 80px
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    if (currentX > 40) {
      // Show delete button
      onSwipe(contact.id);
      setCurrentX(80);
    } else {
      // Reset position
      setCurrentX(0);
      if (isSelected) {
        onSwipe('');
      }
    }
  };

  const handleClick = () => {
    if (isSelected) {
      onSwipe('');
    } else if (currentX === 0) {
      onSelect();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(contact.id);
  };

  useEffect(() => {
    if (!isSelected) {
      setCurrentX(0);
    }
  }, [isSelected]);

  // Avatar URLs used directly from server

  return (
    <div className="relative overflow-hidden">
      <div
        ref={containerRef}
        className="p-3 border-b border-primary/10 hover:bg-card/50 cursor-pointer transition-all duration-300"
        style={{
          transform: `translateX(-${currentX}px)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <Avatar className="h-14 w-14 border-2 border-primary/20">
              <AvatarImage 
                src={contact.avatarUrl} 
                alt={contact.name} 
              />
              <AvatarFallback>{contact.name[0]}</AvatarFallback>
            </Avatar>
            {contact.isOnline && (
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-background"></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {/* First line: Name and time */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <h3 className="font-semibold text-sm truncate">{contact.name}</h3>
                {localFavorites.has(contact.id) && (
                  <Heart className="h-3 w-3 text-red-500 fill-red-500" />
                )}
              </div>
              <span className="text-xs text-muted-foreground">{contact.lastMessageTime}</span>
            </div>
            {/* Second line: Last message and unread count */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground truncate flex-1 mr-2">
                {(() => {
                  // Remove command from beginning if present
                  let text = contact.lastMessage || "";
                  const commandMatch = text.match(/^#\[([A-Z_]+)\]#\s*/);
                  if (commandMatch) {
                    text = text.substring(commandMatch[0].length);
                  }
                  return text;
                })()}
              </p>
              {contact.unreadCount && contact.unreadCount > 0 && (
                <Badge variant="secondary" className="text-xs h-4 bg-primary text-primary-foreground">
                  {contact.unreadCount}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete Button */}
      <div 
        className="absolute right-0 top-0 h-full w-20 bg-red-500 flex items-center justify-center"
        style={{
          transform: `translateX(${80 - currentX}px)`,
        }}
      >
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:text-white hover:bg-red-600"
          onClick={handleDelete}
        >
          <Trash2 className="h-5 w-5" />
        </Button>
      </div>

    </div>
  );
};
